import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { db } from "@/lib/supabase";
import type { Database } from "@/types/database.types";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";

export type DealStage = Database["public"]["Tables"]["deal_stages"]["Row"];

export interface UseDealStagesResult {
  /** All deal stages, ordered by likelihood_to_close ascending. */
  dealStages: DealStage[];
  byId: Map<string, DealStage>;
  byName: Map<string, DealStage>;
  /** The reserved "won" / "lost" stages (identified by `kind`, not name). */
  wonStage: DealStage | undefined;
  lostStage: DealStage | undefined;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const DealStagesContext = createContext<UseDealStagesResult>({
  dealStages: [],
  byId: new Map(),
  byName: new Map(),
  wonStage: undefined,
  lostStage: undefined,
  loading: true,
  error: null,
  refresh: async () => {},
});

export function DealStagesProvider({ children }: { children: React.ReactNode }) {
  const [dealStages, setDealStages] = useState<DealStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await db.dealStages.getAll();
    if (error) {
      setError(error.message ?? "Failed to load deal stages");
    } else {
      setDealStages(data ?? []);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The list is small; a non-silent refresh on remote change won't visibly flicker.
  useRealtimeRefresh(["deal_stages"], refresh);

  const value = useMemo<UseDealStagesResult>(() => {
    const byId = new Map(dealStages.map((s) => [s.id, s]));
    const byName = new Map(dealStages.map((s) => [s.name, s]));
    return {
      dealStages,
      byId,
      byName,
      wonStage: dealStages.find((s) => s.kind === "won"),
      lostStage: dealStages.find((s) => s.kind === "lost"),
      loading,
      error,
      refresh,
    };
  }, [dealStages, loading, error, refresh]);

  return <DealStagesContext.Provider value={value}>{children}</DealStagesContext.Provider>;
}

export function useDealStages(): UseDealStagesResult {
  return useContext(DealStagesContext);
}
